<template>
  <div class="contract_list_container">
    <DxDataGrid
      :element-attr="gridAttributes"
      ref="contractGrid"
      class="contract_grid"
      :data-source="dataSource"
      :no-data-text="$t('common.no_data_found')"
      :show-column-headers="false"
      :hover-state-enabled="true"
      :remote-operations="isRemoteOperation"
      :repaint-changes-only="true"
      :selected-row-keys="selectedContracts"
      @selection-changed="onGridSelectionChanged($event)"
      @row-click="onRowClick"
      height="100%"
      :columns="normalizedColumns"
    >
      <template #contractNumberWithPartnerCell="{ data }">
        <div class="contract_number_partner_cell">
          <div class="contract_number_line">{{ data?.data?.contract_number || "-" }}</div>
          <div class="partner_name_line">{{ data?.data?.partner_name || "-" }}</div>
        </div>
      </template>
      <DxSelection :mode="multiselect ? 'multiple' : 'single'" showCheckBoxesMode="never" selectAllMode="page" />
      <DxFilterRow :visible="true" />
      <DxPaging :enabled="true" />
      <DxScrolling mode="infinite" />
      <DxGrouping :autoExpandAll="true" />
      <DxGroupPanel :visible="false" />
    </DxDataGrid>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { DxDataGrid, DxColumn, DxSelection, DxFilterRow, DxScrolling, DxGrouping, DxGroupPanel, DxPaging } from "devextreme-vue/data-grid";
import { DxScrollView } from "devextreme-vue/scroll-view";
import DataSource from "devextreme/data/data_source";
import CustomStore from "devextreme/data/custom_store";

export default {
  name: "ContractsPurchaseSaleSelector",
  props: {
    localDataset: {
      type: Array,
      default: () => [],
    },
    dataColumns: {
      type: Array,
      default: () => [
        { dataField: "legal_entity_name", caption: "Entitate legală", groupIndex: "0" },
        { dataField: "contract_number", caption: "Număr contract" },
        { dataField: "product_name", caption: "Denumire produs" },
      ],
    },
    hasError: false,
    multiselect: {
      type: Boolean,
      default: () => false,
    },
    isRemoteOperation: {
      type: Boolean,
      default: () => true,
    },
    selectedContracts: {
      type: Array,
      default: () => [],
    },
    filterData: {
      type: Object,
      default: () => ({}),
    },
    toastMessageRef: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      gridAttributes: {
        class: "grid_general_mobile_style",
      },
    };
  },
  components: {
    DxScrollView,
    DxDataGrid,
    DxColumn,
    DxSelection,
    DxFilterRow,
    DxScrolling,
    DxGrouping,
    DxGroupPanel,
    DxPaging,
  },
  computed: {
    normalizedColumns() {
      return (this.dataColumns || []).map((column) => {
        if (column?.dataField !== "contract_number") {
          return column;
        }

        return {
          ...column,
          cellTemplate: "contractNumberWithPartnerCell",
          calculateFilterExpression: this.contractNumberPartnerFilterExpression,
        };
      });
    },
    dataSource() {
      if (this.isRemoteOperation) {
        return this.customStore;
      }

      return this.localDataset;
    },
    customStore() {
      return new DataSource({
        store: new CustomStore({
          //key: "uuid",
          load: async (loadOptions) => {
            if (this.filterData && Object.keys(this.filterData).length > 0) {
              Object.assign(loadOptions, this.filterData);
            }

            const response = await this.getSmContractsPurchaseSales(loadOptions);

            if (loadOptions.group && loadOptions.group.length && loadOptions.group[0].selector === "legal_entity_name") {
              // Group rows by legal_entity_name
              const groups = [];
              const groupMap = {};

              response?.data?.data.forEach((item) => {
                const key = item.legal_entity_name || "Fără entitate";
                if (!groupMap[key]) {
                  groupMap[key] = { key, items: [], count: 0 };
                  groups.push(groupMap[key]);
                }
                groupMap[key].items.push(item);
                groupMap[key].count++;
              });

              return {
                data: groups,
                totalCount: response?.data?.total || 0,
                summary: [],
                groupCount: groups.length,
              };
            }

            return {
              data: response?.data?.data || [],
              totalCount: response?.data?.total || 0,
            };
          },
          byKey: async function (key) {
            return new Promise((resolve) => resolve("success"));
          },
        }),
      });
    },
  },
  methods: {
    ...mapActions({
      getSmContractsPurchaseSales: "smContractsPurchaseSale/getSmContractsPurchaseSales",
    }),
    onGridSelectionChanged(e) {
      this.$emit("update:selectedContracts", e.selectedRowKeys);
    },
    contractNumberPartnerFilterExpression(filterValue, selectedFilterOperation) {
      if (filterValue === null || filterValue === undefined || filterValue === "") {
        return ["contract_number_with_partner", selectedFilterOperation || "contains", filterValue];
      }

      const operation = selectedFilterOperation || "contains";
      return ["contract_number_with_partner", operation, filterValue];
    },
    onRowClick(e) {
      // Prevent default behavior if the row is already selected and multiselect is false
      if (!this.multiselect) {
        return;
      }

      if (e.rowType !== "data") {
        // Not a selectable data row (could be group, header, etc.)
        return;
      }

      const rowKey = e.key; // Get the unique key of the clicked row
      const selectedIndex = this.selectedContracts.indexOf(rowKey);

      if (selectedIndex === -1) {
        // Check if the new selection is valid
        const rowData = e.data; // Get the data of the clicked row
        const newLegalEntityUuid = rowData.legal_entity_uuid;
        const newPartnerUuid = rowData.partner_uuid;
        const newProductUuid = rowData.product_uuid;

        // If nothing is selected, allow selection
        if (this.selectedContracts.length > 0) {
          // Check if all selected items have the same legal_entity_uuid and partner_uuid
          const allSameLegalEntity = this.selectedContracts.every((item) => item.legal_entity_uuid === newLegalEntityUuid);
          const allSamePartner = this.selectedContracts.every((item) => item.partner_uuid === newPartnerUuid);
          const productExists = this.selectedContracts.some((item) => item.product_uuid === newProductUuid && item.uuid !== rowData.uuid);

          if (!allSameLegalEntity) {
            // Show error and reverse selection
            if (this.toastMessageRef) {
              this.toastMessageRef.showError("Toate contractele trebuie să aibă aceeași entitate legală.");
            }
            e.cancel = true; // Prevent the selection change!
            return;
          }
          if (!allSamePartner) {
            // Show error and reverse selection
            if (this.toastMessageRef) {
              this.toastMessageRef.showError("Toate contractele trebuie să aibă același partener.");
            }
            e.cancel = true; // Prevent the selection change!
            return;
          }
          if (productExists) {
            // Show error and reverse selection
            if (this.toastMessageRef) {
              this.toastMessageRef.showError("Produsul selectat există deja în contractele selectate.");
            }
            e.cancel = true; // Prevent the selection change!
            return;
          }
        }

        // If the row is not selected, add it to the selection
        this.selectedContracts.push(rowKey);
      } else {
        // If the row is already selected, remove it from the selection
        this.selectedContracts.splice(selectedIndex, 1);
      }

      // Update the grid's selected rows
      this.$refs.contractGrid.instance.selectRows(this.selectedContracts, false);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/components/dxMobileGridGeneral.scss";

.contract_list_container {
  width: 100%;
  height: 100%;
  padding-left: 10px;
  padding-right: 5px;
}

.contract_list_container ::v-deep .dx-datagrid-rowsview {
  padding-bottom: 80px;
}

.contract_number_partner_cell {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}

.contract_number_line {
  font-weight: 700;
}

.partner_name_line {
  margin-top: 2px;
  color: #6f6f6f;
}
</style>
